import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "./supabaseClient";

const AuthCtx = createContext(null);

async function fetchUserRow(authUserId) {
  const { data, error } = await supabase
    .from("users")
    .select("id, email, name, role, company_id, is_active")
    .eq("id", authUserId)
    .single();
  if (error) return null;
  return data;
}

export function AuthProvider({ children }) {
  // undefined = "still checking", null = "not logged in", object = "logged in"
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    let mounted = true;

    // On first load, check if a Supabase session already exists
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return;
      if (session) {
        const row = await fetchUserRow(session.user.id);
        setUser(row);
      } else {
        setUser(null);
      }
    });

    // Keep in sync with login/logout/token-refresh events from other tabs too
    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!mounted) return;
      if (session) {
        const row = await fetchUserRow(session.user.id);
        setUser(row);
      } else {
        setUser(null);
      }
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  const login = async (email, password, captchaToken) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
      options: captchaToken ? { captchaToken } : undefined,
    });
    if (error) throw new Error(error.message);
    const row = await fetchUserRow(data.user.id);
    if (!row) {
      await supabase.auth.signOut();
      throw new Error("Your account is not set up correctly. Contact your admin.");
    }
    if (!row.is_active) {
      await supabase.auth.signOut();
      throw new Error("This account has been disabled.");
    }
    setUser(row);
    return row;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, login, logout, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
